import type { IBatchStore } from './batch-store-types';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import logger from '../../utils/logger';
import convertUnit from '../../utils/unit-converter';
import {
  getRunMode,
  isRunModeNewYork,
} from './run-mode';
import { ToleranceMode } from '../../constants/Configuration';
import { IBatchData } from '../../containers/Batch/IBatchData';
import { IMaterial } from '../../../types/material/MaterialModels';
import { MixDesignItem } from '../../../types/batch/MixDesignItem';
import { IScale } from '../../../types/device/DeviceModels';
import { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';

export const scaleWeightMixin = {

  setDecumScaleStatus(this: IBatchStore, scaleId: number, status: any = { status: DeviceStatus.FINISH }) {
    const materials = this.materialStore.materials.filter((v: IMaterial) => v.scaleId === scaleId);

    materials.forEach((material: IMaterial) => {
      if (scaleId && scaleId === material.scaleId) {
        const sid = `${scaleId}-${material.code}`;
        const s = this.getDeviceStatus(sid);

        this.deviceStatus.set(sid, {
          ...s,
          id: sid,
          ...status,
          target: status.status === DeviceStatus.FINISH ? s.target : 0,
          startWeight: status.status === DeviceStatus.FINISH ? s.startWeight : 0,
          finishWeight: status.status === DeviceStatus.FINISH ? s.finishWeight : 0,
        });
      }
    });
  },

  getCurrentWeightForBin(this: IBatchStore, binId: number) {
    const bin = this.devicesStore.getDeviceById(binId);
    let weight = 0;

    if (!bin) {
      return weight;
    }

    const scale = this.devicesStore.getDeviceById(bin.scaleId);
    if (!scale) {
      return weight;
    }

    weight = this.getScaleWeight(scale);

    return weight;
  },

  getTargetForWater(this: IBatchStore, constituent: MixDesignItem, data: IBatchData) {
    return (typeof constituent.quantityNet === 'undefined' ? constituent.quantity : constituent.quantityNet) * (data.dropSize || 1);
  },

  getTargetForWeighed(
    this: IBatchStore,
    constituent: MixDesignItem,
    data: IBatchData,
    moisture: number = 0,
    withDropSize: boolean = true,
  ) {
    if (data.materialId && this.materialStore.isAggregate(data.materialId)) {
      return data.quantity;
    }

    let qty;

    if (constituent) {
      qty = typeof constituent.quantityNet === 'undefined'
        ? constituent.quantity
        : constituent.quantityNet;
      moisture = moisture || constituent.moisture || 0;
    } else {
      qty = data.quantity;
    }

    const dropSize = withDropSize ? data.dropSize : 1;
    const target = toNumber((qty + ((moisture*qty)/100)) * dropSize, 2);

    return qty > 0 ? target : 0;
  },

  getAnalogInputValue(this: IBatchStore, analogInputId: number): number {
    const analogInput = this.devicesStore.getDeviceById(analogInputId);

    if (!analogInput?.channelPNo) {
      return 0;
    }

    const m = Number(this.analogInputs[analogInput.channelPNo - 1]);
    const zero = analogInput.zero || 0;
    const span = analogInput.span || 0;

    if (analogInput.useNewFormula) {
      const diff = span / (span - zero);

      return toNumber((diff * (m - zero))/10, 2);
    }

    const min = -20;
    const max = 20;
    const diff = (max-min)/(span-zero);

    return toNumber((((max+m)/diff)) + zero, 2);
  },

  getActualSlumpData(this: IBatchStore, slumpId: number) {
    const mt = this.devicesStore.getDeviceById(slumpId);

    if (!mt?.slumpPNo) {
      return 0;
    }

    const m = Number(this.analogInputs[mt.slumpPNo - 1]);
    const min = -20;
    const max = 20;
    const diff = (max-min)/40;

    return toNumber((((min+m)/diff) - 20), 2);
  },

  checkIfMixIsDifferFromMixInTruck(this: IBatchStore, data: IBatchData, autoFix = false) {
    const { mixStore, materialStore } = this.rootStore;
    let warnings: string[] = [];

    if (data.mixId && data.mixInTruck && data.mixId !== data.mixInTruck) {
      const m1 = data.mixDesign;
      const m2 = mixStore.getMixDesign(Number(data.mixInTruck));

      if (m1 && m2) {
        m1.forEach(v => {
          const c = m2.find((x: MixDesignItem) => x.constituentItemId === v.constituentItemId || (x.batchCode && x.batchCode === v.batchCode));

          if (c && data.batchSize && c.quantity !== v.quantity) {
            const name = materialStore.getConstituentName(v);
            const diff = v.quantity - c.quantity;
            const f = toNumber(v.quantityFinal, 1);
            const qtyToBe = toNumber(v.quantity + ((diff * data.mixQuantityInTruck) / data.batchSize), 1);
            const msg = `${name} quantity in truck is ${c.quantity > v.quantity ? 'greater' : 'lower'}
                        ${c.quantity} <-> ${v.quantity} ${v.unitSymbol}, Mix quantity should be ${qtyToBe},
                        and now is ${f}`;

            logger.log(msg);

            if (f > 0 && qtyToBe !== f) {
              warnings.push(msg);

              if (autoFix) {
                v.trim = toNumber((v.trim  || 0) + (qtyToBe - f), 2);
              }
            }

          }
        });

        m2.forEach((v: MixDesignItem) => {
          const c = m1.find(x => x.constituentItemId === v.constituentItemId || (x.batchCode && x.batchCode === v.batchCode));

          if (!c) {
            const name = materialStore.getConstituentName(v);
            const msg = `Mix in Truck contains ${name} ${v.quantity} ${v.unitSymbol}`;

            logger.log(msg);
            warnings.push(msg);
          }
        });
      }
    }

    return autoFix ? data : warnings;
  },

  adjustMixDesignToMixInTruck(this: IBatchStore, data: IBatchData) {
    return this.checkIfMixIsDifferFromMixInTruck(data, true);
  },

  setAutoStartTare(this: IBatchStore, scale: IScale, runningData: IBatchData) {
    const weight = this.getScaleWeight(scale);
    const targets = runningData.scales[scale.id].map((v: IWeighedDataToBatch) => v.mft).reduce((a: number, b: number) => a + b, 0);

    runningData.scales[scale.id].forEach((data: IWeighedDataToBatch, i: number) => {
      const percent = (100 * data.mft) / targets;
      const deductionWeight = (percent / 100) * weight;
      let newTarget;

      if (percent === 100 && weight > data.mft) {
        newTarget = data.mft;
        data.targetDeduction = data.mft;
      } else if (i === 0) {
        newTarget = data.mft >= deductionWeight ? data.mft + (weight - deductionWeight) : 0;
        data.targetDeduction = deductionWeight * -1;
      } else {
        newTarget = data.mft >= deductionWeight ? data.mft - deductionWeight : 0;
        data.targetDeduction = deductionWeight;
      }

      data.mft = newTarget;

      logger.log(`Auto Start Tare distribution: ${data.material ? data.material.code : ''} ${percent}%`,
        'target:', data.mft, 'deduction:', deductionWeight,
        'new target:', newTarget, 'scale weight:', weight, 'all targets:', targets);

    });
  },

  isScaleReadyForBatch(this: IBatchStore, scaleId: number, batchData: IBatchData): boolean {
    scaleId = Number(scaleId);
    const scale = this.devicesStore.getDeviceById(scaleId);

    if (!scale) {
      logger.warn('No scale with id', scaleId);
      return false;
    }

    const scaleWeight = this.getScaleWeight(scale);
    const ds = this.getDeviceStatus(scale.id);
    let shouldDisplayError = this.checkIfFirstMaterialForScaleIsTurboBatch(batchData, scaleId);
    if (
      !this.running.length
      || (this.running[0].uid === batchData.uid && this.running[0].currentDrop === batchData.currentDrop)
      || (this.settingsStore.configuration.enableTurboTicket && this.running[0]?.mixerId)
    ) {
      shouldDisplayError = true;
    }

    logger.log(`scale #${scale.id} weight=${scaleWeight} > startTare=${scale.startTare}`, ds.status, 'display error:', shouldDisplayError);

    if (ds.status !== DeviceStatus.FINISH && ds.status !== DeviceStatus.IDLE && ds.status !== DeviceStatus.NOT_EMPTY
      && ds.status !== DeviceStatus.OPEN_ERROR) {
      logger.log(`Scale not ready #${scale.id} !`, ds.status);
      return false;
    }

    if (scale.decumulative) {
      if (this.isDecumScaleOpen(scale, true)) {
        this.closeDecumScale(scale);

        return false;
      }

    } else {
      if (scaleWeight > (scale.startTare || 0) && !ds.errorsOverridden.includes(DeviceStatus.NOT_EMPTY)) {
        logger.warn(`Scale weight ${scaleWeight} is over startTare! Scale #${scale.id} startTare=${scale.startTare}!`);
        if (this.settingsStore.configuration.autoStartTare) {
          this.setAutoStartTare(scale, batchData);
        } else {
          if (shouldDisplayError) {
            this.deviceStatus.set(scale.id, {
              ...ds,
              status: DeviceStatus.NOT_EMPTY,
              error: true,
            });
          }
          return false;
        }
      }

      if (this.isInputDisabled(scale.gateOneStatusPNo)) {
        this.closeScale(scale);
      } else if (this.isScaleOpen(scale)) {
        logger.log(`Scale not ready #${scale.id} is open!, Closing ...`);

        if (shouldDisplayError) {
          this.deviceStatus.set(scale.id, {
            ...ds,
            status: DeviceStatus.OPEN_ERROR,
            error: true,
          });
        }

        if (!this.isTestMode) {
          this.closeScale(scale);
        }

        return (this.isTestMode && ds.errorsOverridden.includes(DeviceStatus.OPEN_ERROR)) || false;
      }

      if (this.isDoubleGateScale(scale)) {
        if (this.isInputDisabled(scale.gateTwoStatusPNo)) {
          this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
        } else if (this.isScaleOpen(scale, scale.gateTwoStatusPNo)) {
          logger.log(`Scale not ready #${scale.id} is open!, Closing ...`);

          if (shouldDisplayError) {
            this.deviceStatus.set(scale.id, {
              ...ds,
              status: DeviceStatus.OPEN_ERROR,
              error: true,
            });
          }

          if (!this.isTestMode) {
            this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
          }

          return (this.isTestMode && ds.errorsOverridden.includes(DeviceStatus.OPEN_ERROR)) || false;
        }
      }
    }

    if (ds.error) {
      this.deviceStatus.set(scale.id, {
        ...ds,
        status: DeviceStatus.IDLE,
        error: false,
      });
      this.isOverride = false;
    }

    return true;
  },

  getRemainingTargetForLastDrop(this: IBatchStore, data: IWeighedDataToBatch, runningData: IBatchData): number {
    const { material, materialDetails, scale } = data;

    if (!runningData.mixDesign || runningData.allDrops === 1) {
      return data.mft;
    }

    const scaleUnit = this.getUnitById(scale.unitId);
    const materialUnit = materialDetails.itemBatchUnitSymbol;
    const mItem = runningData.mixDesign.find(v => v.batchCode && v.batchCode === material.batchCode) || runningData.mixDesign.find(v => v.constituentItemId === material.itemId);
    let target = mItem ? mItem.quantityBatchSize : 0;
    let actual = 0;

    const item = runningData.results.find(v => v.itemId === material.itemId);
    if (item) {
      actual = item.actual;
      target = item.target;
    }

    const prev = this.running.find(v => v.uid === runningData.uid && v.currentDrop === runningData.currentDrop - 1);
    if (prev) {
      const pItem = prev.mixDesign.find((v: MixDesignItem) => v.batchCode && v.batchCode === material.batchCode) || prev.mixDesign.find((v: MixDesignItem) => v.constituentItemId === material.itemId);
      if (pItem) {
        actual = actual + pItem.actualWeight;
        target = pItem.quantityBatchSize;
      }

      const item = prev.results.find((v: any) => v.itemId === material.itemId);
      if (item) {
        actual = actual + item.actual;
        target = item.target;
      }
    }

    // Remainder transferred here from a substituted material during this drop; not part of the design target.
    const transferredBySubstitution = mItem?.substitutedQuantity || 0;
    const unconverted = target - actual + transferredBySubstitution;
    const converted = convertUnit(unconverted, materialUnit, scaleUnit);
    logger.log(`jog only on last drop: remaining target from all ${runningData.allDrops} drops: ${material.code}`, unconverted, materialUnit, converted, scaleUnit);

    return toNumber(converted);
  },

  getNetTargetForScale(this: IBatchStore, scaleId: number, runningData: IBatchData) {
    const allFeedFinish = runningData.scales[scaleId]
      .filter((v: IWeighedDataToBatch) => v.isFeedFinish)
      .map((v: IWeighedDataToBatch) => v.mft)
      .reduce((a: number, b: number) => a + b, 0);
    const allFeedFinishProportion = runningData.scales[scaleId]
      .filter((v: IWeighedDataToBatch) => !v.isFeedFinish && v.isFeedFinishProportionOne)
      .map((v: IWeighedDataToBatch) => v.proportionOneActual)
      .reduce((a: number, b: number) => a + b, 0);
    const previouslyFedTarget = runningData.scales[scaleId]
      .reduce((total: number, entry: IWeighedDataToBatch) => total + (entry.previouslyFedTarget || 0), 0);
    return allFeedFinish + allFeedFinishProportion + previouslyFedTarget;
  },

  getBinWeightForStatus(this: IBatchStore, binStatus: any, scaleWeight: number) {
    const { configuration } = this.settingsStore;
    const runMode = getRunMode(configuration, this);
    return isRunModeNewYork(runMode) || configuration?.toleranceMode === ToleranceMode.NYDOTCumulative ? scaleWeight : scaleWeight - binStatus.prevWeight;
  },

  isActiveDrop(this: IBatchStore, initialData: IBatchData) {
    return !!this.running.some(v => v.uid === initialData.uid && v.currentDrop === initialData.currentDrop);
  },

};
